// Holds the logged-in user's email so any component can display it without
// re-reading firebase auth state. Set from App.js on auth-state changes.
import { auth } from './firebase';

let currentUserEmail = '';

export function setCurrentUserEmail(email) {
	currentUserEmail = email || '';
}

export function getCurrentUserEmail() {
	return currentUserEmail;
}

// apiFetch calls the Go backend with the current Firebase ID token attached
// as a Bearer token, so the server can actually verify the caller (replacing
// the old pattern of trusting a client-supplied `loggedInUser` query param).
// Returns the raw fetch Response — callers choose .json() or .blob() as needed.
export async function apiFetch(path, options = {}) {
	if (!auth.currentUser) {
		throw new Error('Not signed in');
	}
	const idToken = await auth.currentUser.getIdToken();
	// A FormData body (a file upload) gets no Content-Type, so the browser can
	// set the multipart boundary itself.
	const isJSON = options.body && !(options.body instanceof FormData);
	const headers = {
		...(isJSON ? { 'Content-Type': 'application/json' } : {}),
		...options.headers,
		Authorization: `Bearer ${idToken}`,
	};
	return fetch(`${process.env.REACT_APP_API_URL}${path}`, { ...options, headers });
}

// fetchReports returns the report catalog metadata (key, label, category,
// description, date_range) used to build the Reports submenu.
export async function fetchReports() {
	const res = await apiFetch('/api/reports');
	if (!res.ok) throw new Error((await res.text()) || 'Failed to load reports');
	return res.json();
}

// runReport executes a single report by key and returns { report, rows }.
// Date-range reports accept optional startDate/endDate (YYYY-MM-DD); any other
// entries are passed through as query params — used for the toggle parameters
// some reports declare (the server validates values against the declared set).
export async function runReport(key, { startDate, endDate, ...extra } = {}) {
	const params = new URLSearchParams();
	if (startDate) params.set('startDate', startDate);
	if (endDate) params.set('endDate', endDate);
	for (const [k, v] of Object.entries(extra)) {
		if (v !== undefined && v !== null && v !== '') params.set(k, v);
	}
	const qs = params.toString();
	const res = await apiFetch(`/api/reports/${encodeURIComponent(key)}${qs ? `?${qs}` : ''}`);
	if (!res.ok) throw new Error((await res.text()) || 'Failed to run report');
	return res.json();
}

// ── Access ───────────────────────────────────────────────────────────────────
// fetchMe returns who the caller is and what their profile lets them see:
// { email, name, role_key, role_name, is_admin, permissions[], catalog? }. The
// app renders itself from this, and the server independently enforces the same
// permissions on every endpoint.
export async function fetchMe() {
	const res = await apiFetch('/api/me');
	if (!res.ok) {
		const text = (await res.text()).trim();
		// The server prefixes these two so the app can tell "you're not set up"
		// apart from a genuine failure and show the right screen.
		const err = new Error(text || 'Failed to load your access');
		if (res.status === 403 && text.startsWith('no-account:')) err.code = 'no-account';
		if (res.status === 403 && text.startsWith('inactive:')) err.code = 'inactive';
		throw err;
	}
	return res.json();
}

// ── Marketing admin ──────────────────────────────────────────────────────────
// CRUD over vendors, campaigns, and daily spends. The server sends plain-text
// error bodies written for the user (e.g. "This vendor has 3 campaigns…"), so
// they're surfaced as-is rather than replaced with a generic message.

async function marketingRequest(path, options) {
	const res = await apiFetch(path, options);
	if (!res.ok) {
		const text = (await res.text()).trim();
		throw new Error(text || 'Request failed');
	}
	// 204 on delete — nothing to parse.
	return res.status === 204 ? null : res.json();
}

const jsonBody = (method, data) => ({ method, body: JSON.stringify(data) });

export const fetchVendors = () => marketingRequest('/api/marketing/vendors');
export const createVendor = (name) => marketingRequest('/api/marketing/vendors', jsonBody('POST', { name }));
export const updateVendor = (id, name) => marketingRequest(`/api/marketing/vendors/${id}`, jsonBody('PUT', { name }));
export const deleteVendor = (id) => marketingRequest(`/api/marketing/vendors/${id}`, { method: 'DELETE' });

export const fetchCampaigns = () => marketingRequest('/api/marketing/campaigns');
export const fetchCampaignTypes = () => marketingRequest('/api/marketing/campaign-types');
export const createCampaign = (data) => marketingRequest('/api/marketing/campaigns', jsonBody('POST', data));
export const updateCampaign = (id, data) => marketingRequest(`/api/marketing/campaigns/${id}`, jsonBody('PUT', data));
export const deleteCampaign = (id) => marketingRequest(`/api/marketing/campaigns/${id}`, { method: 'DELETE' });

export function fetchSpends({ startDate, endDate, campaignId } = {}) {
	const params = new URLSearchParams();
	if (startDate) params.set('startDate', startDate);
	if (endDate) params.set('endDate', endDate);
	if (campaignId) params.set('campaignId', campaignId);
	const qs = params.toString();
	return marketingRequest(`/api/marketing/spends${qs ? `?${qs}` : ''}`);
}

// applySpend writes one amount across a date range, one row per included day.
// Payload: { campaign_id, start_date, end_date, skip_weekends, field, mode, amount }
// where field is "amount" | "estimated_amount" and mode is "total" | "per_day".
export const applySpend = (data) => marketingRequest('/api/marketing/spends/apply', jsonBody('POST', data));

export const updateSpend = (id, data) => marketingRequest(`/api/marketing/spends/${id}`, jsonBody('PUT', data));
export const deleteSpend = (id) => marketingRequest(`/api/marketing/spends/${id}`, { method: 'DELETE' });

export function clearSpendRange({ campaignId, startDate, endDate }) {
	const params = new URLSearchParams({ campaignId, startDate, endDate });
	return marketingRequest(`/api/marketing/spends/range?${params}`, { method: 'DELETE' });
}

// ── Ad spend sync ────────────────────────────────────────────────────────────
// Google Ads / Meta daily spend pulled into Spends, one campaign per ad
// account. A run happens in the background on the server; the page polls
// fetchAdSyncBatch until every account has an outcome.

export const fetchAdSyncStatus = () => marketingRequest('/api/marketing/ad-sync/status');
export const fetchAdAccounts = () => marketingRequest('/api/marketing/ad-sync/accounts');
// data: { campaign_id?, enabled? }
export const updateAdAccount = (id, data) => marketingRequest(`/api/marketing/ad-sync/accounts/${id}`, jsonBody('PUT', data));
// data: {} for the usual window, or { start_date, end_date } for a backfill.
export const startAdSync = (data = {}) => marketingRequest('/api/marketing/ad-sync/runs', jsonBody('POST', data));
export const fetchAdSyncBatch = (batchId) => marketingRequest(`/api/marketing/ad-sync/batches/${encodeURIComponent(batchId)}`);
export const fetchAdSyncRuns = (limit = 50) => marketingRequest(`/api/marketing/ad-sync/runs?limit=${limit}`);

// ── Blocked leads (suppression list) ─────────────────────────────────────────
// The phones/emails whose website form submissions quick-api drops. Same
// plain-text error convention as the marketing endpoints.

export const fetchSuppression = () => marketingRequest('/api/suppression');
export const createSuppression = (data) => marketingRequest('/api/suppression', jsonBody('POST', data));
export const updateSuppression = (id, data) => marketingRequest(`/api/suppression/${id}`, jsonBody('PUT', data));
export const deleteSuppression = (id) => marketingRequest(`/api/suppression/${id}`, { method: 'DELETE' });

// ── Canopy sync ──────────────────────────────────────────────────────────────
// Look IRS Logics cases up, then create (or update) the clients in Canopy. A
// batch runs in the background on the server — one signed-in browser works
// through the cases — so the page polls fetchCanopyBatch until done.

export const lookupCanopyCases = (caseNumbers) =>
	marketingRequest('/api/canopy/lookup', jsonBody('POST', { case_numbers: caseNumbers }));
// sendToTaxPrep: also push each case to Quick Tax Prep (the firm's
// return-preparation app); the server ignores it when that isn't configured.
export const createCanopyBatch = (caseNumbers, sendToTaxPrep = true) =>
	marketingRequest('/api/canopy/batches', jsonBody('POST', { case_numbers: caseNumbers, send_to_taxprep: sendToTaxPrep }));
export const fetchCanopyBatch = (batchId) => marketingRequest(`/api/canopy/batches/${encodeURIComponent(batchId)}`);
export const fetchCanopySyncs = (limit = 50) => marketingRequest(`/api/canopy/syncs?limit=${limit}`);
export const fetchTaxPrepSyncs = (limit = 50) => marketingRequest(`/api/canopy/taxprep/syncs?limit=${limit}`);

// ── Commissions ──────────────────────────────────────────────────────────────
// Agents (everyone who earns commission: a name, optionally linked to an app
// user), cases (one or more invoices, each a commissionable amount + split
// agents; the settlement officer on each payment gets the rest; payments fill
// the invoices in order unless pinned to one), uploads of the IRS Logics payment report,
// and what everyone has earned. Same plain-text error convention as the
// marketing endpoints. (The agents endpoints keep their first name, openers.)

export const fetchCommissionCases = () => marketingRequest('/api/commissions/cases');
export const createCommissionCase = (data) => marketingRequest('/api/commissions/cases', jsonBody('POST', data));
export const updateCommissionCase = (caseNumber, data) =>
	marketingRequest(`/api/commissions/cases/${encodeURIComponent(caseNumber)}`, jsonBody('PUT', data));
export const deleteCommissionCase = (caseNumber) =>
	marketingRequest(`/api/commissions/cases/${encodeURIComponent(caseNumber)}`, { method: 'DELETE' });

export const fetchCommissionOpeners = () => marketingRequest('/api/commissions/openers');
export const createCommissionOpener = (data) => marketingRequest('/api/commissions/openers', jsonBody('POST', data));
export const updateCommissionOpener = (id, data) => marketingRequest(`/api/commissions/openers/${id}`, jsonBody('PUT', data));
export const deleteCommissionOpener = (id) => marketingRequest(`/api/commissions/openers/${id}`, { method: 'DELETE' });

// App users as { id, name, email, active, opener_id }, for linking an opener
// to a login. opener_id is set when the user already is an opener.
export const fetchCommissionUsers = () => marketingRequest('/api/commissions/users');

// The report goes up as multipart form data.
const reportUpload = (path, file) => {
	const form = new FormData();
	form.append('file', file);
	return marketingRequest(path, { method: 'POST', body: form });
};

// previewPaymentReport parses a report and says what importing it would do,
// without saving anything; importPaymentReport saves it. Both return the same
// summary (the import's also carries the saved `import` record).
export const previewPaymentReport = (file) => reportUpload('/api/commissions/imports/preview', file);
export const importPaymentReport = (file) => reportUpload('/api/commissions/imports', file);
export const fetchPaymentImports = () => marketingRequest('/api/commissions/imports');

export function fetchCommissionPayments({ caseNumber, startDate, endDate } = {}) {
	const params = new URLSearchParams();
	if (caseNumber) params.set('case', caseNumber);
	if (startDate) params.set('start', startDate);
	if (endDate) params.set('end', endDate);
	const qs = params.toString();
	return marketingRequest(`/api/commissions/payments${qs ? `?${qs}` : ''}`);
}

// fetchEarnedCommissions returns { start, end, totals[], lines[] }: commission
// earned by payments dated in the range, per agent and per payment, by split
// agents and settlement officers. agentId may be '0': the officer share of
// payments that named no settlement officer.
export function fetchEarnedCommissions({ startDate, endDate, agentId } = {}) {
	const params = new URLSearchParams();
	if (startDate) params.set('start', startDate);
	if (endDate) params.set('end', endDate);
	if (agentId !== undefined && agentId !== '') params.set('agent_id', agentId);
	const qs = params.toString();
	return marketingRequest(`/api/commissions/earned${qs ? `?${qs}` : ''}`);
}

// ── Users & profiles (admin) ─────────────────────────────────────────────────
// Same plain-text error convention as the marketing endpoints, so the dialogs
// can surface the server's own wording.

export const fetchUsers = () => marketingRequest('/api/users');
export const createUser = (data) => marketingRequest('/api/users', jsonBody('POST', data));
export const updateUser = (id, data) => marketingRequest(`/api/users/${id}`, jsonBody('PUT', data));
export const deleteUser = (id) => marketingRequest(`/api/users/${id}`, { method: 'DELETE' });

export const fetchRoles = () => marketingRequest('/api/roles');
export const createRole = (data) => marketingRequest('/api/roles', jsonBody('POST', data));
export const updateRole = (id, data) => marketingRequest(`/api/roles/${id}`, jsonBody('PUT', data));
export const deleteRole = (id) => marketingRequest(`/api/roles/${id}`, { method: 'DELETE' });

// The permission tree the profile editor renders (sections, marketing pages,
// and every report), built by the server so a new report needs no frontend
// change.
export const fetchPermissionCatalog = () => marketingRequest('/api/permissions');

// Every marketing vendor as { id, name }, for limiting a profile to some of
// them. Served under the user-management permission rather than the Marketing
// one, and never narrowed by the caller's own limit.
export const fetchScopeVendors = () => marketingRequest('/api/permissions/vendors');
